import { serializeError } from 'serialize-error';
import { getNewAudioContext } from '../../../../../helpers/media';
import type { NoiseSuppressionNode } from '../../../../../types/streamEffects';
import type Logger from '../../../../Logger';
import type { AudioTrackProcessor } from '../AudioTrackProcessor';
import NoiseSuppressionAudioWorkletNode from './NoiseSuppressionAudioWorkletNode';
import NoiseSuppressionNodeProvider from './NoiseSuppressionNodeProvider';

interface TrackProcessorParams {
  noiseSuppressor: WebAssembly.WebAssemblyInstantiatedSource;
  loggerFactory: (namespace: string) => Logger;
}

interface ProcessingContext {
  audioContext: AudioContext;
  streamDestination: MediaStreamAudioDestinationNode;
  noiseSuppressionNode: NoiseSuppressionNode;
  noiseSuppressionProvider: NoiseSuppressionNodeProvider;
}

export default class RNNoiseTrackProcessor implements AudioTrackProcessor {
  readonly #noiseSuppressor: WebAssembly.WebAssemblyInstantiatedSource;

  #processingContext?: ProcessingContext;

  readonly #logger: Logger;

  #track?: MediaStreamTrack;

  readonly type = 'rnnoise';

  constructor(params: TrackProcessorParams) {
    this.#noiseSuppressor = params.noiseSuppressor;
    this.#logger = params.loggerFactory('TrackProcessor');
  }

  get isProcessing(): boolean {
    return !!this.#track;
  }

  release(): void {
    this.stopProcessing();
  }

  async processTrack(track: MediaStreamTrack): Promise<MediaStreamTrack> {
    const context = getNewAudioContext();

    if (!context) {
      throw new Error('AudioContext is not available');
    }

    const destination = new MediaStreamAudioDestinationNode(context, {
      channelCountMode: 'explicit',
      channelCount: 1,
      channelInterpretation: 'speakers',
    });

    await NoiseSuppressionAudioWorkletNode.register(context);
    const source = context.createMediaStreamSource(new MediaStream([track]));
    const nsNodeProvider = new NoiseSuppressionNodeProvider({ context, noiseSuppressor: this.#noiseSuppressor });
    const nsNode = await nsNodeProvider.createNoiseSuppressionNode();
    nsNode.connect(destination);
    source.connect(nsNode);
    const [processedTrack] = destination.stream.getTracks();

    this.#processingContext = {
      audioContext: context,
      streamDestination: destination,
      noiseSuppressionNode: nsNode,
      noiseSuppressionProvider: nsNodeProvider,
    };

    this.#track = processedTrack;

    this.#logger.info('processTrack()', {
      case: 'Denoiser',
      type: this.type,
      track: track.id,
    });

    return processedTrack;
  }

  stopProcessing(): void {
    if (!this.isProcessing) {
      this.#logger.debug('stopProcessing()', {
        description: 'Track is not processed',
        case: 'Denoiser',
        type: this.type,
      });

      return;
    }

    if (!this.#processingContext) {
      this.#logger.debug('stopProcessing()', {
        success: false,
        description: 'Processing context is not available',
        case: 'Denoiser',
        type: this.type,
      });

      return;
    }

    try {
      this.#processingContext.noiseSuppressionProvider.stop();
      this.#processingContext.noiseSuppressionNode.disconnect();
      this.#processingContext.streamDestination.disconnect();
      this.#processingContext.audioContext.close().catch((error: unknown) => {
        this.#logger.error('stopProcessing()', {
          success: false,
          error: serializeError(error),
          case: 'Denoiser',
          type: this.type,
        });
      });
      this.#track?.stop();
      this.#track = undefined;
      this.#logger.info('stopProcessing()', {
        success: true,
        case: 'Denoiser',
        type: this.type,
      });
    } catch (error) {
      this.#logger.error('stopProcessing()', {
        success: false,
        error: serializeError(error),
        case: 'Denoiser',
        type: this.type,
      });
    }
  }
}
